import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';

export const ALL_PERMISSIONS = [
  { key: 'dashboard', label: 'Dashboard' },
  { key: 'templates', label: 'Templates' },
  { key: 'orders', label: 'Bestellungen' },
  { key: 'customers', label: 'Kunden' },
  { key: 'invoices', label: 'Rechnungen' },
  { key: 'products', label: 'Produkte' },
  { key: 'seo', label: 'SEO' },
  { key: 'funnel', label: 'Funnel' },
  { key: 'e2e_tests', label: 'E2E Tests' },
  { key: 'coupons', label: 'Gutscheine' },
  { key: 'webhooks', label: 'Webhooks' },
  { key: 'settings', label: 'Einstellungen' },
] as const;

export type PermissionKey = typeof ALL_PERMISSIONS[number]['key'];

export function useAdminPermissions() {
  const { user, loading: authLoading, isAdmin, isSuperAdmin } = useAuth();
  const shouldLoadPermissions = !!user && !authLoading && isAdmin && !isSuperAdmin;

  const { data: permissions = [], isLoading } = useQuery({
    queryKey: ['admin-permissions', user?.id],
    queryFn: async () => {
      if (!user) return [];
      const { data, error } = await supabase
        .from('admin_permissions' as any)
        .select('permission')
        .eq('user_id', user.id);
      if (error) throw error;
      return (data || []).map((d: any) => d.permission as string);
    },
    enabled: shouldLoadPermissions,
    staleTime: 30 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const hasPermission = (key: PermissionKey): boolean => {
    if (isSuperAdmin) return true;
    if (!isAdmin) return false;
    return permissions.includes(key);
  };

  return { permissions, hasPermission, isLoading: shouldLoadPermissions && isLoading, isSuperAdmin };
}
